import { ApiError } from '../../api/client';
import { Card } from '../../components/arc/card/card';
import { PageState } from '../../components/arc/page-state/page-state';
import { getMarketingDashboard, type MarketingDashboard, type DashboardService } from '../../api/marketing';
import { useRondoPageData } from '../useRondoPageData';
import './marketing-overview-arc.css';

const SERVICES: { key: string; name: string; tick: string }[] = [
  { key: 'scheduler', name: 'Scheduler', tick: '30 s' },
  { key: 'worker', name: 'Worker', tick: '5 s' },
  { key: 'mailkit_stats', name: 'Mailkit stats sync', tick: '60 s (adaptivně)' },
];

const KPIS: { key: 'pending' | 'processing' | 'sent' | 'fail' | 'active'; label: string }[] = [
  { key: 'active', label: 'Aktivní kampaně' },
  { key: 'sent', label: 'Odesláno za 30 dní' },
  { key: 'pending', label: 'Ve frontě' },
  { key: 'processing', label: 'Zpracovává se' },
  { key: 'fail', label: 'Chyby za 30 dní' },
];

function fmtAge(iso: string | null): string {
  if (!iso) return '-';
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} h`;
  return `${Math.floor(s / 86400)} d`;
}
function fmtDate(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('cs-CZ', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
function health(status: DashboardService['status']): 'healthy' | 'stale' | 'idle' {
  if (status === 'ok' || status === 'running') return 'healthy';
  if (status === 'fail') return 'stale';
  return 'idle';
}
const HEALTH_LABEL: Record<string, string> = { healthy: 'Aktivní', stale: 'Nečinný', idle: 'Neběží' };

export function MarketingPrehledPage() {
  const { data, error, loading, updatedAt } = useRondoPageData<MarketingDashboard>('marketing-dashboard', getMarketingDashboard, 15000);

  if (loading) return <PageState title="Načítám marketingový přehled" loading />;
  if (error) return <PageState title="Marketingový přehled se nepodařilo načíst" message={error instanceof ApiError ? error.message : 'Načtení selhalo'} />;
  if (!data) return null;

  const svcByKey = Object.fromEntries(data.services.map((s) => [s.key, s]));
  const kpiVal: Record<string, number> = {
    pending: data.queue.pending, processing: data.queue.processing,
    sent: data.stats.sent30d, fail: data.stats.errors30d, active: data.activeCampaigns,
  };
  const kpiNote: Record<string, string> = {
    pending: 'aktuálně', processing: 'aktuálně',
    sent: `30 dní · 7d: ${data.stats.sent7d.toLocaleString('cs-CZ')}`,
    fail: `30 dní · 7d: ${data.stats.errors7d.toLocaleString('cs-CZ')}`,
    active: 'Otevřít seznam kampaní',
  };

  return (
    <section className="rondo-marketing-page rondo-marketing-overview rondo-arc-marketing-overview p1-arc-scope">
      <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">MARKETING</div><h1>Přehled</h1><p>Stav komunikačních služeb a kampaní</p></div><span className="prototype-updated">{updatedAt ? `Aktualizováno ${new Date(updatedAt).toLocaleTimeString('cs-CZ')}` : ''}</span></header>

      <div className="rondo-arc-marketing-kpis">
        {KPIS.map((k) => {
          return <Card title={k.label} className="rondo-arc-marketing-kpi" key={k.key}>
            <strong>{(kpiVal[k.key] ?? 0).toLocaleString('cs-CZ')}</strong>
            {k.key === 'active' ? <a href="/marketing/kampane">{kpiNote[k.key]}</a> : <p>{kpiNote[k.key]}</p>}
          </Card>;
        })}
      </div>

      <div className="rondo-arc-marketing-heading"><h2>Komunikační infrastruktura</h2><p>Živý stav služeb, které plánují, odesílají a vyhodnocují kampaně.</p></div>
      <div className="rondo-arc-marketing-services">
        {SERVICES.map((cfg) => <ServiceCard key={cfg.key} cfg={cfg} svc={svcByKey[cfg.key]} dash={data} />)}
      </div>
    </section>
  );
}

function ServiceCard({ cfg, svc, dash }: { cfg: { key?: string; name: string; tick: string }; svc?: DashboardService; dash: MarketingDashboard }) {
  const h = svc ? health(svc.status) : 'idle';
  const n = (v: number) => v.toLocaleString('cs-CZ');
  const q = dash.queue;
  const domain: { label: string; value: string } | null =
    cfg.key === 'scheduler' ? { label: 'Kampaně', value: n(dash.activeCampaigns ?? 0) }
    : cfg.key === 'worker' ? { label: 'Joby', value: `${n(q.total ?? 0)} (chyby ${n(q.failed ?? 0)}, skip ${n(q.skipped ?? 0)})` }
    : cfg.key === 'mailkit_stats' ? { label: 'Odesláno', value: `${n(q.sent ?? 0)} · API volání ${n(svc?.counter ?? 0)}` }
    : null;
  return (
    <Card title={cfg.name} className="rondo-arc-marketing-service">
      <span className={`rondo-arc-marketing-status ${h}`}>{HEALTH_LABEL[h]}</span>
      <div className="rondo-arc-marketing-service-meta">
        <span>Tick</span><strong>{cfg.tick} · {fmtAge(svc?.lastRunAt ?? null)} zpět</strong>
        <span>Spuštěn</span><strong>{fmtDate(svc?.lastSuccessAt ?? null)}</strong>
        <span>Celkem ticků</span><strong>{(svc?.counter ?? 0).toLocaleString('cs-CZ')}</strong>
        {domain ? <><span>{domain.label}</span><strong>{domain.value}</strong></> : null}
        {svc?.lastError ? <><span>Chyba</span><strong className="is-error">{svc.lastError.slice(0, 80)}</strong></> : null}
      </div>
    </Card>
  );
}
